import React, { FormEvent, useEffect, useRef, useState } from 'react';

import { IComponentFieldsGeneral } from '../../../types/types';
import { HeaderFields } from '../types/navigationItem.types';
import MenuTriggerContentWithIcon from './menuTriggerContentWithIcon';
import NavigationItemMenuStructure from './navigationItemMenuStructure';
import { QueryParamsConst } from '../../../constants/query-params.constants';
import Suggestions from '../../searchResults/components/suggestions';
import { WithSitecoreContextProps } from '@sitecore-jss/sitecore-jss-react/types/enhancers/withSitecoreContext';
import useQueryParams from '../../../hooks/useQueryParams.effect';
import { documentService, windowService } from '../../../utils/window.utils';
import Icon from '../../@shared/icon';
import { iconSize } from '../../../constants/icon.constants';
import { googleNavClickEvent } from '../../../utils/analytics';
import { topIconMenu } from '../../../constants/analytics.constants';

const SearchMenu = ({ fields, sitecoreContext }: WithSitecoreContextProps & { fields: IComponentFieldsGeneral }) => {
  const [page] = useQueryParams(QueryParamsConst.PAGE, '1');
  const [perPage] = useQueryParams(QueryParamsConst.PER_PAGE, '10');
  const queryKey = QueryParamsConst.QUERY;
  /* eslint-disable-next-line */
  const inputRef = useRef<any>(null);
  const formRef = useRef<HTMLFormElement | null>(null);
  const [, setSuggestionQuery] = useState('');
  const [isSuggestionsComponentVisible, setIsSuggestionsComponentVisible] = useState(false);

  const SUGGESTIONS_MIN_CHAR_LIMIT = 3;

  let submitUrl: URL;
  try {
    const href = fields.search?.search?.value?.href;
    const origin = windowService()?.location?.origin;

    submitUrl = href.startsWith('/') ? new URL(href, origin) : new URL(href);
  } catch (e) {
    console.warn(e);
  }
  const onSubmit: (e: FormEvent<HTMLFormElement>) => void = (e) => {
    e.preventDefault();
    const inputEl = inputRef.current;
    if (submitUrl && inputEl && !submitUrl.searchParams.has(queryKey)) {
      submitUrl.searchParams.append(queryKey, inputEl.value);
      submitUrl.searchParams.append(QueryParamsConst.PAGE, page);
      submitUrl.searchParams.append(QueryParamsConst.PER_PAGE, perPage);
      windowService().location.href = submitUrl.toString();
    }
  };

  const updateSuggestionsQuery = (event) => {
    setSuggestionQuery(event?.target?.value);
  };

  const onKeyUp: (e) => void = (e) => {
    if (e?.key === 'Enter') {
      formRef.current?.dispatchEvent(new Event('submit', {cancelable: true, bubbles: true}));
      return;
    }

    updateSuggestionsQuery(e);
  };

  const onUpdateQueryFromSuggestions = (q: string) => {
    inputRef.current.value = q;
    setIsSuggestionsComponentVisible(false);
    formRef.current?.dispatchEvent(new Event('submit', {cancelable: true, bubbles: true}));
  };

  const handleClickOutside = (event) => {
    if (formRef?.current?.contains(event?.target)) {
      return;
    }

    setIsSuggestionsComponentVisible(false);
  };

  useEffect(() => {
    documentService().addEventListener('mouseup', handleClickOutside);
    return () => {
      documentService().removeEventListener('mouseup', handleClickOutside);
    };
  }, []);

  return (
    <NavigationItemMenuStructure
      triggerContent={<MenuTriggerContentWithIcon iconField={fields?.search?.icon} textValue={fields.search?.link?.value?.text} />}
      onclick={() => {
        googleNavClickEvent(topIconMenu, fields?.search?.link?.value?.href, fields?.search?.link?.value?.text);
      }}
    >
      <div className={'navigation__menu-item__tray__container navigation__menu-item__tray__container--search'}>
        <form action={fields.search?.search?.value?.href} onSubmit={onSubmit} ref={formRef}>
          <div className="header-search-popup">
            <div className="search__input-row">
              <div className="search__input-control">
                <Icon iconName="action-search" size={iconSize.dsk} extraClassName="search__input-search-icon" />
                <div className="search__input-control-container">
                  <input
                    name="Search"
                    placeholder={fields.search?.placeholder?.value}
                    type="text"
                    className="search-popup__input"
                    ref={inputRef}
                    onKeyUp={onKeyUp}
                    onFocus={() => setIsSuggestionsComponentVisible(true)}
                    autoComplete="off"
                  />
                </div>
              </div>
              <button type={'submit'} disabled={!inputRef?.current?.value} className={'button p-orange search__button'}>
                {fields.search?.search?.value?.text}
              </button>
            </div>
            {isSuggestionsComponentVisible && (
              <Suggestions
                suggestionQuery={inputRef?.current?.value}
                language={sitecoreContext?.language || ''}
                contextId={sitecoreContext?.itemId || ''}
                minimalLength={SUGGESTIONS_MIN_CHAR_LIMIT}
                onUpdateQuery={onUpdateQueryFromSuggestions}
              />
            )}
          </div>
        </form>
      </div>
    </NavigationItemMenuStructure>
  );
};

const Search = ({ fields, sitecoreContext }: WithSitecoreContextProps & { fields: HeaderFields }) => {
  return <SearchMenu fields={fields} sitecoreContext={sitecoreContext} />;
};

export default Search;
